import React, { PureComponent } from 'react';
import ReactTooltip from 'react-tooltip';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import Select from '../../shared/reactSelect/select.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import { OptionPropTypes, ColumnDefinitionsPropTypes } from '../../shared/propTypes/common';
import TaxExpenseTEDView from '../shared/taxExpenseTEDView.component';
import TaxExpenseGridView from '../shared/taxExpenseGridView.component';
import HamburgerDropdown from '../../balanceSheetManagement/shared/hamburgerDropdown.component';
import SecondaryMenu from '../../menu/secondaryMenu.component';
import TaxExpenseDetails from './taxExpenseDetails.component';


import {
  periodsSelector,
  periodOrCurrentSelector,
  extraReportingCurrencyCodesSelector,
  reportingCurrencyCodeSelector,
  environmentSuffixSelector,
} from '../../shared/store/selectors';

import {
  fetchInitialDataForViewTaxExpensePage,
  selectPeriodForViewTaxExpensePage,
  selectBookTypeForViewTaxExpensePage,
  selectViewTypeForViewTaxExpensePage,
  selectGroupTypeForViewTaxExpensePage,
  exportTaxExpenseDataForAllPills,
  exportTaxExpenseCsvForAllPills,
  selectReportingCurrencyForViewTaxExpensePage,
  selectCompanyCodeToFilter,
  selectCustomGroup,
  selectCountryTaxTypeForViewTaxExpensePage,
  selectDetailAggregationForTaxExpensePage,
  selectReportBookType,
  selectReportBookTypes,
} from './store/actions';
import {
  isExportingReportForAllPillsSelector,
  companyCodesOptionsSelector,
  companyCodeToFilterSelector,
  customGroupOptionsSelector,
  customGroupSelector,
  countryTaxTypeToFilterSelector,
  countryTaxTypesOptionsSelector,
  selectedCountryToTaxTypeDescriptionSelector,
  detailAggregationSelector,
  columnDefsSelector,
  selectedReportBookTypesSelector,
  reportBookTypesOptionsSelector,
  selectedReportBookTypeOptionsSelector,
  REPORT_BOOK_TYPES_ALL_OPTION,
} from './store/selectors';

import {
  viewTypes,
  historicalGroupTypes,
  groupTypes,
  groupTypeColumns,
  groupTypeValues,
  ProvisionStatusNames,
} from './constants';
import { FUNCTIONAL_CURRENCY } from '@common/constants/sharedConstants';
import { BOOK_TYPES } from '../shared/constants';
import {
  getLocalStorageKey,
  updateLocalStorage,
} from '../../shared/customReports/localStorage';

const rebuildReactTooltip = debounce(() => {
  ReactTooltip.hide();
  ReactTooltip.rebuild();
}, 200);

const reportingCurrencyCodesSelector = extraReportingCurrencyCodesSelector(['Functional Currency']);

const draftStatuses = new Set([
  ProvisionStatusNames.IN_PROCESS,
  ProvisionStatusNames.ERROR,
]);

class ViewTaxExpensePage extends PureComponent {
  static propTypes = {
    useHistorical: PropTypes.bool,

    periods: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
    })).isRequired,
    fetchInitialDataForViewTaxExpensePage: PropTypes.func.isRequired,
    selectPeriodForViewTaxExpensePage: PropTypes.func.isRequired,
    selectBookTypeForViewTaxExpensePage: PropTypes.func.isRequired,
    selectViewTypeForViewTaxExpensePage: PropTypes.func.isRequired,
    selectGroupTypeForViewTaxExpensePage: PropTypes.func.isRequired,
    selectDetailAggregationForTaxExpensePage: PropTypes.func.isRequired,
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
    reportBookType: PropTypes.string.isRequired,
    selectedReportBookTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    viewType: PropTypes.string.isRequired,
    groupType: PropTypes.string.isRequired,
    detailAggregationType: PropTypes.string.isRequired,
    reportData: PropTypes.shape({
      name: PropTypes.string,
      orientation: PropTypes.string,
      rows: PropTypes.arrayOf(PropTypes.shape({
        rowType: PropTypes.string,
        description: PropTypes.string,
        columns: PropTypes.arrayOf(PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })),
      })),
      rawSource: PropTypes.oneOfType([
        PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        PropTypes.array,
      ]),
      columnCount: PropTypes.number,
    }),
    reportDataByBookType: PropTypes.objectOf(PropTypes.shape({})),
    taxExpenseLoading: PropTypes.bool.isRequired,
    exportTaxExpenseDataForAllPills: PropTypes.func.isRequired,
    exportTaxExpenseCsvForAllPills: PropTypes.func.isRequired,
    isExportingReportForAllPills: PropTypes.bool.isRequired,

    reportingCurrencyCode: PropTypes.string.isRequired,
    reportingCurrencyCodes: PropTypes.arrayOf(OptionPropTypes),
    selectReportingCurrencyCode: PropTypes.func.isRequired,
    selectCompanyCodeToFilter: PropTypes.func.isRequired,
    companyCodesOptions: PropTypes.arrayOf(OptionPropTypes),
    companyCodeToFilter: PropTypes.string,
    environmentSuffix: PropTypes.string.isRequired,
    selectCustomGroup: PropTypes.func.isRequired,
    customGroupOptions: PropTypes.arrayOf(OptionPropTypes),
    customGroup: PropTypes.string,

    countryTaxTypesOptions: PropTypes.arrayOf(OptionPropTypes),
    countryTaxType: PropTypes.string,
    countryTaxTypeDescription: PropTypes.string,
    selectCountryTaxType: PropTypes.func.isRequired,

    columnDefs: ColumnDefinitionsPropTypes,
    useExpandableDetailsList: PropTypes.bool,
    selectReportBookType: PropTypes.func.isRequired,
    selectReportBookTypes: PropTypes.func.isRequired,
    reportBookTypesOptions: PropTypes.arrayOf(OptionPropTypes),
    selectedReportBookTypeOptions: PropTypes.arrayOf(OptionPropTypes),
  };

  componentDidMount() {
    const {
      period,
      reportBookType: reportBookTypeProp,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      fetchInitialDataForViewTaxExpensePage,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      useExpandableDetailsList,
      selectReportBookType,
    } = this.props;

    let reportBookType = reportBookTypeProp;
    let selectedReportBookTypesToUse = selectedReportBookTypes;

    if (!useHistorical) {
      selectedReportBookTypesToUse = this.getPersistedSelectedReportBookTypes() || selectedReportBookTypes;

      if (!selectedReportBookTypesToUse.length) {
        selectedReportBookTypesToUse = Object.values(BOOK_TYPES);
      }

      if (!selectedReportBookTypesToUse.includes(reportBookType)) {
        reportBookType = selectedReportBookTypesToUse[0];
      }

      selectReportBookType({
        reportBookType,
        selectedReportBookTypes: selectedReportBookTypesToUse,
      });
      this.persistSelectedReportBookTypes(selectedReportBookTypesToUse);
    } else if (reportBookTypeProp === BOOK_TYPES.US_SUMMARY) {
      reportBookType = BOOK_TYPES.US_FED;
      selectReportBookType({
        reportBookType: BOOK_TYPES.US_FED,
        selectedReportBookTypes,
      });
    }

    fetchInitialDataForViewTaxExpensePage({
      period,
      reportBookType,
      selectedReportBookTypes: selectedReportBookTypesToUse,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  }

  componentDidUpdate({ taxExpenseLoading: wasTaxExpenseLoading }) {
    if (wasTaxExpenseLoading && !this.props.taxExpenseLoading) {
      ReactTooltip.rebuild();
    }
  }

  getTaxExpensePillsStorageKey = () => getLocalStorageKey('tax-expense', 'selected-book-types');

  getPersistedSelectedReportBookTypes = () => {
    try {
      const savedState = JSON.parse(window.localStorage.getItem(this.getTaxExpensePillsStorageKey()));
      const persistedValue = savedState?.selectedReportBookTypes;
      if (!savedState || !persistedValue) {
        return null;
      }

      const parsed = JSON.parse(persistedValue);
      if (!Array.isArray(parsed)) {
        return null;
      }

      const validBookTypes = parsed.filter(bookType => Object.values(BOOK_TYPES).includes(bookType));
      return validBookTypes.length ? validBookTypes : null;
    } catch {
      return null;
    }
  };

  persistSelectedReportBookTypes = selectedReportBookTypes => {
    try {
      updateLocalStorage({
        keyname: this.getTaxExpensePillsStorageKey(),
        state: {
          selectedReportBookTypes: JSON.stringify(selectedReportBookTypes),
        },
      });
    } catch {
      return null;
    }
  };

  selectPeriod = selectedPeriod => {
    const {
      periods,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      useExpandableDetailsList,
    } = this.props;
    const value = selectedPeriod ? selectedPeriod.value : null;
    const period = periods.find(period => period.name === value);

    this.props.selectPeriodForViewTaxExpensePage({
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  };

  selectBookType = selectedBookType => {
    const {
      period,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      useExpandableDetailsList,
      selectedReportBookTypes,
      reportDataByBookType,
    } = this.props;

    const countryTaxType = selectedBookType === BOOK_TYPES.US_STATE ? this.props.countryTaxType : 'ALL';

    this.props.selectBookTypeForViewTaxExpensePage({
      period,
      reportBookType: selectedBookType,
      selectedReportBookTypes,
      reportDataByBookType,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  };

  selectViewType = selectedViewType => {
    const viewType = selectedViewType ? selectedViewType.value : null;
    this.props.selectViewTypeForViewTaxExpensePage(viewType);
  };

  selectGroupType = selectedGroupType => {
    const {
      period,
      reportBookType,
      selectedReportBookTypes,
      reportingCurrencyCode,
      useHistorical,
      customGroup,
      countryTaxType,
      useExpandableDetailsList,
    } = this.props;
    const groupType = selectedGroupType ? selectedGroupType.value : null;
    this.props.selectGroupTypeForViewTaxExpensePage({
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  };

  selectReportingCurrencyCode = selectedReportingCurrencyCode => {
    const {
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      selectReportingCurrencyCode,
      customGroup,
      countryTaxType,
      useExpandableDetailsList,
    } = this.props;
    if (!reportingCurrencyCode || reportingCurrencyCode.value !== selectedReportingCurrencyCode.value) {
      selectReportingCurrencyCode({
        period,
        reportBookType,
        selectedReportBookTypes,
        groupType,
        reportingCurrencyCode: selectedReportingCurrencyCode.value,
        useHistorical,
        companyCodeToFilter,
        customGroup,
        countryTaxType,
        generateAsyncDetailsLink: useExpandableDetailsList,
      });
    }
  };

  selectCompanyCodeToFilter = companyCodeToFilter => {
    const {
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      selectCompanyCodeToFilter,
      customGroup,
      countryTaxType,
      useExpandableDetailsList,
    } = this.props;

    selectCompanyCodeToFilter({
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  };

  selectReportBookTypes = selectedBookTypes => {
    const {
      period,
      reportBookType,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      reportBookTypesOptions,
      useExpandableDetailsList,
      selectReportBookTypes,
      selectBookTypeForViewTaxExpensePage,
      reportDataByBookType,
    } = this.props;

    const selectedBookTypeValues = (selectedBookTypes || []).map(({ value }) => value);
    const allSelectableBookTypes = reportBookTypesOptions
      .filter(({ value }) => value !== REPORT_BOOK_TYPES_ALL_OPTION)
      .map(({ value }) => value);
    let normalizedSelectedReportBookTypes = [reportBookType];

    if (selectedBookTypeValues.includes(REPORT_BOOK_TYPES_ALL_OPTION)) {
      normalizedSelectedReportBookTypes = allSelectableBookTypes;
    } else if (selectedBookTypeValues.length) {
      normalizedSelectedReportBookTypes = selectedBookTypeValues;
    }
    const nextReportBookType = normalizedSelectedReportBookTypes.includes(reportBookType)
      ? reportBookType
      : normalizedSelectedReportBookTypes[0];

    this.persistSelectedReportBookTypes(normalizedSelectedReportBookTypes);
    selectReportBookTypes(normalizedSelectedReportBookTypes);

    selectBookTypeForViewTaxExpensePage({
      period,
      reportBookType: nextReportBookType,
      selectedReportBookTypes: normalizedSelectedReportBookTypes,
      reportDataByBookType,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType: nextReportBookType === BOOK_TYPES.US_STATE ? countryTaxType : 'ALL',
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  };

  selectCustomGroup = customGroup => {
    const {
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      selectCustomGroup,
      countryTaxType,
      useExpandableDetailsList,
    } = this.props;

    selectCustomGroup({
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  };

  selectCountryTaxType = countryTaxType => {
    const {
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      selectCountryTaxType,
      useExpandableDetailsList,
    } = this.props;

    selectCountryTaxType({
      period,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink: useExpandableDetailsList,
    });
  };

  selectDetailAggregationType = selectedDetailAggregationType => {
    const detailAggregationType = selectedDetailAggregationType ? selectedDetailAggregationType.value : null;
    this.props.selectDetailAggregationForTaxExpensePage(detailAggregationType);
  };

  buildExportParams = () => {
    const {
      viewType,
      reportBookType,
      selectedReportBookTypes,
      groupType,
      period,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      environmentSuffix,
      customGroup,
      countryTaxType,
      countryTaxTypeDescription,
      detailAggregationType,
      useExpandableDetailsList,
    } = this.props;

    return {
      groupType,
      period,
      reportBookType,
      selectedReportBookTypes,
      viewType,
      hasSourceCurrency: reportingCurrencyCode === FUNCTIONAL_CURRENCY,
      reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      environmentSuffix,
      countryTaxType,
      countryTaxTypeDescription,
      detailAggregationType,
      useExpandableDetailsList,
    };
  };

  exportToExcel = () => {
    if (!this.props.viewType) {
      return;
    }
    this.props.exportTaxExpenseDataForAllPills(this.buildExportParams());
  };

  exportToCsv = () => {
    if (!this.props.viewType) {
      return;
    }
    this.props.exportTaxExpenseCsvForAllPills(this.buildExportParams());
  };

  render() {
    const {
      reportData,
      reportBookType,
      viewType,
      groupType,
      selectedReportBookTypeOptions,
      reportBookTypesOptions,
      taxExpenseLoading,
      isExportingReportForAllPills,
      reportingCurrencyCode,
      reportingCurrencyCodes,
      useHistorical,
      companyCodesOptions,
      companyCodeToFilter,
      customGroupOptions,
      customGroup,
      countryTaxTypesOptions,
      countryTaxType,
      columnDefs: colDefsToUse,
      useExpandableDetailsList,
    } = this.props;

    const rawSource = Array.isArray(reportData?.rawSource) ? reportData.rawSource : [];
    const shouldShowDraftBanner = rawSource
      .some(({ STATUS_NAME }) => draftStatuses.has(STATUS_NAME));

    return (
      <div className="view-tax-expense-page">
        <SecondaryMenu>
          <div className="aggregation-menu">
            <div className="aggregation-group">
              <div className="aggregation-selector">
                <PeriodDropdown
                  onChange={this.selectPeriod}
                  maxHeight={420}
                />
                {
                  !useHistorical && reportBookType === BOOK_TYPES.US_STATE && (
                    <Select
                      className="select-country-tax-type"
                      options={countryTaxTypesOptions}
                      name="countryTaxType"
                      onChange={this.selectCountryTaxType}
                      scrollMenuIntoView={false}
                      value={countryTaxType}
                      clearable={false}
                      virtualized
                    />
                  )
                }
              </div>
              <div className="aggregation-selector">
                <Select
                  className="select-view-types"
                  options={reportBookTypesOptions}
                  name="bookTypes"
                  onChange={this.selectReportBookTypes}
                  scrollMenuIntoView={false}
                  value={selectedReportBookTypeOptions}
                  clearable={false}
                  multi
                  virtualized
                />
              </div>
              <div className="aggregation-selector">
                <Select
                  className="select-view-types"
                  clearable={false}
                  name="groupType"
                  options={useHistorical ? historicalGroupTypes : groupTypes}
                  onChange={this.selectGroupType}
                  scrollMenuIntoView={false}
                  value={groupType}
                  virtualized
                />
              </div>
              {
                !useHistorical && groupType === groupTypeValues.CUSTOM_GROUP ? (
                  <div className="aggregation-selector">
                    <Select
                      className="select-company-min"
                      clearable={false}
                      options={customGroupOptions}
                      onChange={this.selectCustomGroup}
                      value={customGroup}
                      virtualized
                    />
                  </div>
                )
                  : null
              }
              {
                groupType === groupTypeValues.COMPANY_CODES
                  ? (
                    <div className="aggregation-selector">
                      <Select
                        className="select-company-min"
                        clearable={false}
                        options={companyCodesOptions}
                        onChange={this.selectCompanyCodeToFilter}
                        value={companyCodeToFilter}
                        virtualized
                      />
                    </div>
                  )
                  : null
              }
              <div className="aggregation-selector">
                <Select
                  className="select-currency"
                  clearable={false}
                  options={reportingCurrencyCodes}
                  value={reportingCurrencyCode}
                  onChange={this.selectReportingCurrencyCode}
                />
              </div>
              <div className={'aggregation-selector ml-auto'}>
                <Select
                  className="select-view-types"
                  clearable={false}
                  name="viewType"
                  options={viewTypes}
                  onChange={this.selectViewType}
                  scrollMenuIntoView={false}
                  value={viewType}
                  virtualized
                />
              </div>
            </div>
            <div className="hamburger-dropdown text-right">
              <HamburgerDropdown
                exportToExcel={this.exportToExcel}
                exportToCsv={this.exportToCsv}
                isExportingReportForAllPills={isExportingReportForAllPills}
              />
            </div>
          </div>
        </SecondaryMenu>
        <div className="page">
          <div className="container-fluid page-content-container">
            <div className="row no-gutters">
              <div className="col">
                {viewType && viewType === 'columns' ? <TaxExpenseTEDView
                  reportData={reportData}
                  reportTitlePrefix={useHistorical ? 'Historical ' : ''}
                  bookType={reportBookType}
                  selectedBookTypes={this.props.selectedReportBookTypes}
                  selectBookType={this.selectBookType}
                  taxExpenseLoading={taxExpenseLoading}
                  useHistorical={useHistorical}
                  shouldShowDraftBanner={shouldShowDraftBanner}
                  disableDrillLink={useExpandableDetailsList}
                  showExpandCollapseAllButton={useExpandableDetailsList}
                /> : null}
                {viewType && viewType === 'rows' ? <TaxExpenseGridView
                  reportData={reportData}
                  columnDefs={colDefsToUse}
                  rebuildReactTooltip={rebuildReactTooltip}
                  bookType={reportBookType}
                  selectedBookTypes={this.props.selectedReportBookTypes}
                  selectBookType={this.selectBookType}
                  taxExpenseLoading={taxExpenseLoading}
                  groupTypeColumn={groupTypeColumns[groupType]}
                  shouldShowDraftBanner={shouldShowDraftBanner}
                  useHistorical={useHistorical}
                /> : null}
                <TaxExpenseDetails />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state, props) {
  const { reports } = state;

  return {
    period: periodOrCurrentSelector(state),
    periods: periodsSelector(state),
    reportData: reports.taxExpense.reportData,
    reportDataByBookType: reports.taxExpense.reportDataByBookType,
    reportBookType: reports.taxExpense.reportBookType,
    selectedReportBookTypes: selectedReportBookTypesSelector(state),
    viewType: reports.taxExpense.viewType,
    groupType: reports.taxExpense.groupType,
    taxExpenseLoading: reports.taxExpense.taxExpenseLoading,
    isExportingReportForAllPills: isExportingReportForAllPillsSelector(state),
    reportingCurrencyCode: reportingCurrencyCodeSelector(state),
    reportingCurrencyCodes: reportingCurrencyCodesSelector(state),
    companyCodesOptions: companyCodesOptionsSelector(state),
    companyCodeToFilter: companyCodeToFilterSelector(state),
    environmentSuffix: environmentSuffixSelector(state),
    customGroupOptions: customGroupOptionsSelector(state),
    customGroup: customGroupSelector(state),
    countryTaxType: countryTaxTypeToFilterSelector(state),
    countryTaxTypesOptions: countryTaxTypesOptionsSelector(state),
    countryTaxTypeDescription: selectedCountryToTaxTypeDescriptionSelector(state),
    detailAggregationType: detailAggregationSelector(state),
    columnDefs: columnDefsSelector(state, props),
    reportBookTypesOptions: reportBookTypesOptionsSelector(state, props),
    selectedReportBookTypeOptions: selectedReportBookTypeOptionsSelector(state, props),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialDataForViewTaxExpensePage,
    selectPeriodForViewTaxExpensePage,
    selectBookTypeForViewTaxExpensePage,
    selectViewTypeForViewTaxExpensePage,
    selectGroupTypeForViewTaxExpensePage,
    selectReportingCurrencyCode: selectReportingCurrencyForViewTaxExpensePage,
    selectCompanyCodeToFilter,
    selectCustomGroup,
    selectCountryTaxType: selectCountryTaxTypeForViewTaxExpensePage,
    selectDetailAggregationForTaxExpensePage,
    exportTaxExpenseDataForAllPills,
    exportTaxExpenseCsvForAllPills,
    selectReportBookType,
    selectReportBookTypes,
  }
)(ViewTaxExpensePage);
